import { startOfWeek } from 'date-fns';
import { formatDateWithoutHours, vorzeitigGarantiertClass } from '@/core/common/helpers/utils';

// Builder- und Format-Helper für die Tooltips der Touristischen Planung

export function getLastMondayFormatted() {
  return formatDateWithoutHours(startOfWeek(new Date(), { weekStartsOn: 1 }));
}

// Liefert das aktuelle Touristische-Planung-Ergebnis eines Reisetermins
export function getLatestTouristischePlanungErgebnis(data) {
  return data?.touristischePlanungErgebnis ?? null;
}

export function hasTouristischePlanungErgebnis(data) {
  return !!getLatestTouristischePlanungErgebnis(data);
}

// Status-Box im einheitlichen Tooltip-Design, getönter Hintergrund (Farbe + 26 Alpha),
// farbiger Rand und Text in der Akzentfarbe sowie weicher Schatten
function buildStatusBox(text, color, subtext = null) {
  const hasSubtext = subtext != null;
  const mainFontSize = hasSubtext ? 10 : 14;
  const mainFontWeight = hasSubtext ? 700 : 600;
  const subHtml = hasSubtext
    ? `<span style="font-size:7px;font-weight:400;margin-top:1px;">${subtext}</span>`
    : '';
  return (
    '<span style="display:inline-flex;flex-direction:column;align-items:center;justify-content:center;' +
    `width:33px;height:28px;background:${color}26;` +
    `border:0.5px solid ${color};border-radius:7px;` +
    'box-shadow:0px 1px 2px -1px #0000001A, 0px 1px 3px 0px #0000001A;' +
    `color:${color};line-height:1;">` +
    `<span style="font-size:${mainFontSize}px;font-weight:${mainFontWeight};">${text}</span>${subHtml}</span>`
  );
}

// Rendert den FVC-Status als farbige Status-Box
// - "N" rot:    Flug nicht verfügbar
// - "RQ" grau:  Verfügbarkeit muss erst angefragt werden
// - "J" grün:   verfügbar ohne Aufpreis
// - "<x>€":     verfügbar mit Aufpreis (grün bis 200 €, sonst rot)
// Der Flughafen wird als Subtext unter dem Hauptstatus angezeigt
function formatFvcStatusHtml(status, aufpreis, flughafen) {
  if (!status) return '-';
  const green = '#1BC5BD';
  const red = '#F64E60';
  const gray = '#B5B5C3';
  // Bei fehlendem Flughafen wird ein "-" als Subtext angezeigt
  const subtext = flughafen || '-';

  if (status === 'NichtVerfuegbar') return buildStatusBox('N', red, subtext);
  if (status === 'AufAnfrage') return buildStatusBox('RQ', gray, subtext);
  if (status === 'Verfuegbar') {
    if (aufpreis == null || aufpreis === 0) return buildStatusBox('J', green, subtext);
    // Schwellwert 200 € entscheidet, ob der Aufpreis als unkritisch (grün) oder teuer (rot) angezeigt wird
    const color = aufpreis > 200 ? red : green;
    return buildStatusBox(`${aufpreis}€`, color, subtext);
  }
  return status;
}

// Rendert die Landverfügbarkeit als farbige Status-Box mit der Anzahl freier Plätze
// - rot ("N"):  keine Daten zur Landverfügbarkeit vorhanden
// - grün:       freie Plätze decken das zusätzliche PAX-Potenzial vollständig ab
// - orange:     freie Plätze sind kleiner als das benötigte Potenzial (teilweise verfügbar)
function formatLandverfuegbarkeitHtml(personenFrei, paxPotenzial) {
  const sufficient = '#1BC5BD';
  const partial = '#FFA800';
  const notAvailable = '#F64E60';
  if (personenFrei == null) return buildStatusBox('N', notAvailable);
  const bedarf = paxPotenzial ?? 0;
  const color = personenFrei >= bedarf ? sufficient : partial;
  return buildStatusBox(personenFrei, color);
}

function formatVtgStatusHtml(vtg) {
  const iconClass = vorzeitigGarantiertClass(vtg);
  return iconClass ? `<i class="${iconClass}"></i>` : '-';
}

function formatRang(rang) {
  if (rang == null) return '-';
  return Number(rang).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Baut das Datenobjekt für den FC-Pax-Potenzial-Tooltip auf
export function buildPaxPotenzialTooltip(data) {
  const latestErgebnis = getLatestTouristischePlanungErgebnis(data);
  if (!latestErgebnis && data?.touristischePlanungRanking == null) return null;
  const ergebnis = latestErgebnis || {};
  return {
    title: getLastMondayFormatted(),
    valuesRight: false,
    rows: [
      { label: 'Rang', value: formatRang(data?.touristischePlanungRanking), bold: true },
      { label: 'Ist Pax', value: ergebnis.istPax ?? '-', bold: true },
      {
        label: 'Zusätzliches PAX Potential',
        value: data?.zusaetzlichesPaxPotenzial ?? '-',
        bold: true,
      },
      {
        label: 'FVC Status',
        html: true,
        value: formatFvcStatusHtml(ergebnis.fvcStatus, ergebnis.aufpreis, ergebnis.fvcFlughafen),
      },
      {
        label: 'Landverfügbarkeit',
        html: true,
        value: formatLandverfuegbarkeitHtml(ergebnis.personenFrei, data?.zusaetzlichesPaxPotenzial),
      },
      {
        label: 'VTG Status',
        html: true,
        value: formatVtgStatusHtml(ergebnis.vtg),
      },
    ],
  };
}

// Baut das Datenobjekt für den FC-Umsatz-Tooltip auf
export function buildFcUmsatzTooltip(data, currency) {
  const latestErgebnis = getLatestTouristischePlanungErgebnis(data);
  if (!latestErgebnis) return null;
  const ergebnis = latestErgebnis;
  const fmt = v => currency(v || 0);
  return {
    title: getLastMondayFormatted(),
    valuesRight: true,
    rows: [
      { label: 'Ist Umsatz', value: fmt(ergebnis.istUmsatzControlling), bold: true },
      {
        label: 'Zusätzliches Umsatzpotenzial',
        value: fmt(data?.zusaetzlichesUmsatzpotenzial),
        bold: true,
      },
      { label: 'Flug-Limitation', value: fmt(ergebnis.flugLimitation), bold: true },
      { label: 'Land-Limitation', value: fmt(ergebnis.landLimitation), bold: true },
      { label: 'Ist Pax > FC PAX', value: fmt(ergebnis.istPaxUeberFcPax), bold: true },
      { label: 'Ist VK > Soll VK', value: fmt(ergebnis.istVkUeberSollVk), bold: true },
    ],
  };
}
